import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import PageHeader from './PageHeader.jsx';
import Loader, { ErrorBox } from './Loader.jsx';
import SortableTh from './SortableTh.jsx';
import OrganisationDetailsLockModal from './OrganisationDetailsLockModal.jsx';
import { columnIsSortable, useTableSort } from '../lib/tableSort.js';
import { useAuth } from '../lib/auth';


function extraParams(resource, user) {
  if (typeof resource.listParams === 'function') return resource.listParams(user) || {};
  if (resource.listParams && typeof resource.listParams === 'object') return { ...resource.listParams };
  return {};
}

/**
 * Generic CRUD list table.
 *
 * Props
 * -----
 * @param {object}   resource     - resource definition (slug, label, api, columns…).
 * @param {function} basePath     - (slug) => '/business' or '/super-admin'.
 * @param {string}   [subtitle]   - optional subtitle.
 *
 * The component fetches `resource.api.list({ search })`, supports a
 * search box, and renders one row per record with Edit / Delete
 * buttons.  The "format" hints on each column are honoured here:
 *
 *   - `format: 'bool'` — render ✓ / ✗.
 *   - `format: 'date'` — render the first 10 chars (YYYY-MM-DD).
 *   - default          — render the value as-is.
 */
export default function ResourceList({ resource, basePath, subtitle }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [lockTarget, setLockTarget] = useState(null);

  const params = useMemo(() => {
    const p = extraParams(resource, user);
    if (search) p.search = search;
    return p;
  }, [search, resource, user]);

  const columns = useMemo(
    () => (resource.columns || []).filter((c) => !(typeof c.hideIf === 'function' && c.hideIf(user))),
    [resource, user],
  );

  const reload = () => {
    setLoading(true);
    resource.api.list(params)
      .then((data) => setItems(data.results || data))
      .catch(setError)
      .finally(() => setLoading(false));
  };

  useEffect(reload, [resource.slug, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDelete = async (row) => {
    if (resource.detailsLock && row.details_locked) {
      setError('This organisation is locked. Unlock it with the PIN before deleting it.');
      return;
    }
    if (!window.confirm(`Delete "${row.name || row.email || row.id}"? This cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      await resource.api.remove(row.id, extraParams(resource, user));
      setItems((current) => current.filter((r) => r.id !== row.id));
    } catch (e) {
      setError(e);
    } finally {
      setBusyId(null);
    }
  };

  const renderCell = (col, row) => {
    const v = row[col.key];
    if (col.format === 'bool') return v ? '✓' : '✗';
    if (col.format === 'lock') {
      return v
        ? <span className="ty-badge ty-badge--locked">Locked</span>
        : <span className="ty-badge ty-badge--open">Open</span>;
    }
    if (col.format === 'date' && typeof v === 'string') return v.slice(0, 10);
    if (Array.isArray(v)) return `${v.length} item(s)`;
    if (v === null || v === undefined || v === '') return '—';
    return String(v);
  };

  const { sortedRows, sort, toggleSort } = useTableSort(items, { columns });

  return (
    <>
      <PageHeader
        title={`${resource.icon ? resource.icon + ' ' : ''}${resource.label}`}
        subtitle={subtitle}
        actions={
          <Link className="ty-btn" to={`${basePath}/${resource.slug}/new`}>
            + New {resource.singular}
          </Link>
        }
      />

      <div className="ty-filters">
        <input
          className="ty-input"
          placeholder={`Search ${resource.label.toLowerCase()}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {resource.listHint ? <p className="ty-muted ty-lock-hint">{resource.listHint}</p> : null}

      <ErrorBox error={error} />

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <div className="ty-empty">No {resource.label.toLowerCase()} yet.</div>
      ) : (
        <div className="ty-table-wrap">
          <table className="ty-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <SortableTh
                    key={c.key}
                    label={c.label}
                    sortKey={c.key}
                    activeKey={sort.key}
                    direction={sort.dir}
                    onSort={toggleSort}
                    sortable={columnIsSortable(c)}
                  />
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => {
                const detailsLocked = !!resource.detailsLock && !!row.details_locked;
                const deleteHidden = typeof resource.canDelete === 'function' && !resource.canDelete(row);
                return (
                <tr key={row.id}>
                  {columns.map((c) => (
                    <td key={c.key} data-label={c.label}>
                      {renderCell(c, row)}
                    </td>
                  ))}
                  <td data-label="" style={{ whiteSpace: 'nowrap' }}>
                    {(typeof resource.rowActions === 'function'
                      ? resource.rowActions(row)
                      : resource.rowActions || []
                    ).map((action) => (
                      <button
                        key={action.label}
                        className="ty-btn ty-btn--ghost ty-btn--sm"
                        onClick={() => {
                          if (action.href) {
                            window.location.assign(action.href);
                            return;
                          }
                          navigate(action.to);
                        }}
                        style={{ marginRight: 6 }}
                      >
                        {action.label}
                      </button>
                    ))}
                    {resource.detailsLock && (
                      <button
                        type="button"
                        className="ty-btn ty-btn--ghost ty-btn--sm"
                        onClick={() => setLockTarget(row)}
                        style={{ marginRight: 6 }}
                      >
                        {detailsLocked ? 'Unlock' : 'Lock'}
                      </button>
                    )}
                    <button
                      className="ty-btn ty-btn--ghost ty-btn--sm"
                      onClick={() => navigate(`${basePath}/${resource.slug}/${row.id}/edit`)}
                    >
                      {detailsLocked ? 'View' : 'Edit'}
                    </button>{' '}
                    {!deleteHidden && (
                      <button
                        className="ty-btn ty-btn--danger ty-btn--sm"
                        onClick={() => onDelete(row)}
                        disabled={busyId === row.id || detailsLocked}
                        title={detailsLocked ? 'Unlock this organisation with its PIN before deleting it.' : undefined}
                      >
                        {busyId === row.id ? '…' : 'Delete'}
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lockTarget && resource.detailsLock && (
        <OrganisationDetailsLockModal
          organisation={lockTarget}
          lockDetails={resource.api.lockDetails}
          unlockDetails={resource.api.unlockDetails}
          onClose={() => setLockTarget(null)}
          onSaved={(updated) => {
            setItems((current) => current.map((row) => (
              row.id === (updated.id || lockTarget.id) ? { ...row, ...updated } : row
            )));
            setLockTarget(null);
          }}
        />
      )}
    </>
  );
}
